import type { Persona } from "@shopify-harness/sdk";
import { ShopifyHarnessApiError } from "@shopify-harness/sdk";
import { createFileRoute, type ErrorComponentProps, Link } from "@tanstack/react-router";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import CopyIcon from "lucide-react/dist/esm/icons/copy";
import { toast } from "sonner";

import { PageBody, Panel } from "@/components/shell/page-frame";
import { EmptyState, ErrorState } from "@/components/states/state-views";
import { StatusBadge } from "@/components/status/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { personaStatusPresentation } from "@/features/personas/persona-presentation";
import { buildPersonaPrompt, hasPersonaProposal } from "@/features/personas/persona-prompt";
import { personaQueryOptions } from "@/features/personas/queries";
import { formatDateTime, formatInteger, formatPercent } from "@/lib/format";

export const Route = createFileRoute("/personas/$personaId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      personaQueryOptions(context.api.personas, params.personaId)
    ),
  pendingComponent: PersonaDetailLoading,
  errorComponent: PersonaDetailError,
  component: PersonaDetailPage
});

/**
 * ペルソナ詳細。プロフィールの読み物と、その根拠となるデータを 1 画面で読む分析系の画面。
 * 編集操作は持たない（生成・更新は Claude Code の MCP 経由）。
 */
function PersonaDetailPage() {
  const persona = Route.useLoaderData();
  const presentation = personaStatusPresentation(persona.status);

  return (
    <>
      {/* 詳細専用ヘッダー。一覧への戻り口を持つため PageHeader とは別に組む。 */}
      <div className="sticky top-(--topbar-h) z-30 flex min-h-(--pagehead-h) flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="ペルソナ一覧へ戻る"
          render={<Link to="/personas" />}
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-lg font-semibold tracking-tight text-fg">{persona.name}</h1>
          <StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge>
          {persona.segment ? <Badge variant="outline">{persona.segment}</Badge> : null}
        </div>
        <span className="hidden text-xs text-fg-subtle lg:inline">
          {persona.shopDomain.replace(".myshopify.com", "")} · 更新{" "}
          {formatDateTime(persona.updatedAt)}
        </span>
      </div>

      <PageBody density="roomy" className="grid gap-6">
        {/* 結論から読む。人物像より先に「このペルソナで何をすべきか」を置く（レポート詳細と同じ構成）。 */}
        <Panel
          title="AI提案"
          description="セグメント割り当て → シナリオ追加など、施策につなげる優先度順の提案"
          actions={<CopyPromptButton persona={persona} />}
        >
          {persona.recommendation || persona.recommendedActions.length > 0 ? (
            <>
              {persona.recommendation ? (
                <p className="text-sm leading-relaxed text-fg">{persona.recommendation}</p>
              ) : null}
              {persona.recommendedActions.length > 0 ? (
                <ol className="mt-3 grid gap-1.5">
                  {persona.recommendedActions.map((action, index) => (
                    <li key={action} className="flex items-start gap-2 text-sm text-fg">
                      <span className="num mt-0.5 grid size-4 shrink-0 place-items-center rounded-sm bg-surface-sunken text-2xs font-semibold text-fg-muted">
                        {index + 1}
                      </span>
                      {action}
                    </li>
                  ))}
                </ol>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-fg-subtle">
              AI提案が未生成です。upsert_persona で recommendation と recommended_actions
              を投入すると、ここに施策案が表示されます。
            </p>
          )}
        </Panel>

        <Panel title="概要" description={persona.tagline}>
          <p className="text-sm leading-relaxed whitespace-pre-line text-fg">{persona.summary}</p>
          {persona.quote ? (
            <blockquote className="mt-4 border-l-2 border-line-strong pl-3 text-sm text-fg-muted italic">
              「{persona.quote}」
            </blockquote>
          ) : null}
          {persona.preferredChannels.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              <span className="eyebrow mr-1">好む接点</span>
              {persona.preferredChannels.map((channel) => (
                <Badge key={channel} variant="secondary">
                  {channel}
                </Badge>
              ))}
            </div>
          ) : null}
        </Panel>

        <div className="grid gap-4 sm:grid-cols-2">
          <TraitPanel title="属性" items={persona.demographics} />
          <TraitPanel title="行動特性" items={persona.behaviors} />
          <TraitPanel title="ゴール" items={persona.goals} />
          <TraitPanel title="課題・不満" items={persona.painPoints} />
        </div>

        <Panel
          title="データの裏付け"
          description="このペルソナの生成根拠。数値はストアの実データからの推定"
        >
          <dl className="grid gap-4 sm:grid-cols-3">
            <MetricEntry
              label="該当顧客数"
              value={
                persona.contactCount !== null
                  ? `${formatInteger(persona.contactCount)}人`
                  : "未算出"
              }
            />
            <MetricEntry
              label="売上構成比"
              value={persona.revenueShare !== null ? formatPercent(persona.revenueShare) : "未算出"}
            />
            <MetricEntry label="生成元" value={persona.generatedBy ?? "不明"} />
          </dl>

          {persona.evidence.length > 0 ? (
            <ul className="mt-4 grid gap-1.5 border-t border-line pt-4">
              {persona.evidence.map((entry) => (
                <li key={entry} className="flex items-start gap-2 text-sm text-fg">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-strong"
                  />
                  {entry}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 border-t border-line pt-4 text-sm text-fg-subtle">
              根拠データが記録されていません。再生成時に evidence を含めると、ここに表示されます。
            </p>
          )}
        </Panel>
      </PageBody>
    </>
  );
}

/**
 * 提案あり → Harness に実設定させる「設定プロンプト」、
 * 提案なし → 提案そのものを作らせる「生成プロンプト」をコピーする。
 * 画面は閲覧専用のため、「コピーして Claude Code に貼る」ことが提案を動かす唯一の導線になる。
 */
function CopyPromptButton({ persona }: { persona: Persona }) {
  const isSetup = hasPersonaProposal(persona);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPersonaPrompt(persona));
      toast.success(
        isSetup
          ? "Harness への設定プロンプトをコピーしました"
          : "AI提案の生成プロンプトをコピーしました",
        {
          description: isSetup
            ? "Claude Code に貼り付けると、セグメント割当とシナリオ登録が MCP 経由で実行されます"
            : "Claude Code に貼り付けて実行すると、MCP 経由で提案が生成されます"
        }
      );
    } catch {
      toast.error("クリップボードにコピーできませんでした", {
        description: "ブラウザの権限設定を確認してください"
      });
    }
  };

  return (
    <Button type="button" size="sm" variant="outline" onClick={() => void handleCopy()}>
      <CopyIcon aria-hidden="true" />
      {isSetup ? "設定プロンプトをコピー" : "生成プロンプトをコピー"}
    </Button>
  );
}

function TraitPanel({ title, items }: { title: string; items: string[] }) {
  return (
    <Panel title={title} count={items.length > 0 ? formatInteger(items.length) : undefined}>
      {items.length > 0 ? (
        <ul className="grid gap-1.5">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-fg">
              <span
                aria-hidden="true"
                className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line-strong"
              />
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-subtle">未設定</p>
      )}
    </Panel>
  );
}

function MetricEntry({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-0.5">
      <dt className="eyebrow">{label}</dt>
      <dd className="num text-lg font-semibold text-fg">{value}</dd>
    </div>
  );
}

function PersonaDetailLoading() {
  return (
    <PageBody density="roomy" className="grid gap-6">
      <div role="status" aria-label="読み込み中" className="grid gap-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-40 w-full" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    </PageBody>
  );
}

function PersonaDetailError({ error, reset }: ErrorComponentProps) {
  if (error instanceof ShopifyHarnessApiError && error.status === 404) {
    return (
      <PageBody density="roomy">
        <EmptyState
          title="ペルソナが見つかりません"
          description="削除されたか、URL が誤っている可能性があります。一覧から選び直してください。"
          action={
            <Button size="sm" variant="outline" render={<Link to="/personas" />}>
              ペルソナ一覧へ戻る
            </Button>
          }
        />
      </PageBody>
    );
  }

  return (
    <PageBody density="roomy">
      <ErrorState
        title="ペルソナを読み込めませんでした"
        message={error.message}
        {...(error instanceof ShopifyHarnessApiError && error.requestId
          ? { requestId: error.requestId }
          : {})}
        onRetry={reset}
      />
    </PageBody>
  );
}
